import { useEffect, useMemo, useRef, useState } from "react";
import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import { userVerificationAttempt } from "@/hooks/firestore/models/userVerifications";
import { verificationService } from "../services/verification";
import { useVerificationStore } from "../stores/verificationStore";
import { buildVerificationErrorInfo } from "../utils/verificationError";
import type { VerificationErrorInfo } from "../types/VerificationError";
import { useVerificationReadiness } from "@/hooks/firestore/user/useVerificationReadiness";

const verificationAttemptsModel = userVerificationAttempt.array();

/**
 * If the user navigates to the verification tab too quickly after signup, they
 * could get there before we get a reply from Sumsub which results in the below
 * error code. This system polls the endpoint for a max of 60 seconds until
 * it gets some data, if it still errors after that we display the error
 */
const RETRYABLE_ERROR_CODE = "user-verification-not-found";
const MAX_RETRIES = 30;
const RETRY_DELAY_MS = 2000;

export const useVerificationPlan = () => {
  const { setSelectedAttemptId, clearSelectedAttemptId, selectedAttemptId } =
    useVerificationStore();
  const {
    ready: verificationPathReady,
    isLoading: readinessLoading,
    error: readinessError,
  } = useVerificationReadiness();

  const requestedRef = useRef<boolean>(false);
  const retriesRef = useRef<number>(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [requestError, setRequestError] =
    useState<VerificationErrorInfo | null>(null);
  const [isRequestLoading, setIsRequestLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  // If the user receives a verification plan response with no paths that means
  // they are unable to self-verify and have to contact support
  const [hasNoPaths, setHasNoPaths] = useState(false);

  const {
    data: attempts,
    isLoading: attemptsLoading,
    error: firestoreError,
  } = useFirestore<typeof verificationAttemptsModel, "attemptId", string>(
    "/users/{userId}/verifications/verificationPlan/attempts",
    userVerificationAttempt.array(),
    { keyPath: "attemptId" },
  );

  const attemptRequestPlan = () => {
    verificationService.requestPlan().then((result) => {
      if (!result.ok) {
        const isRetryableError =
          result.error &&
          "code" in result.error &&
          result.error.code === RETRYABLE_ERROR_CODE;

        if (isRetryableError && retriesRef.current < MAX_RETRIES) {
          retriesRef.current += 1;
          setIsRequestLoading(false);
          setIsRetrying(true);
          retryTimerRef.current = setTimeout(
            attemptRequestPlan,
            RETRY_DELAY_MS,
          );
          return;
        }

        setIsRetrying(false);
        setIsRequestLoading(false);
        setRequestError(
          buildVerificationErrorInfo({ source: "plan", cause: result.error }),
        );
        return;
      }

      retriesRef.current = 0;
      setIsRetrying(false);
      setIsRequestLoading(false);

      if (result.data.paths.length) {
        setSelectedAttemptId(result.data.paths[0].attemptId);
      } else {
        setHasNoPaths(true);
      }
    });
  };

  const runRequestPlan = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
    }

    retriesRef.current = 0;

    clearSelectedAttemptId();
    setRequestError(null);
    setIsRetrying(false);
    setHasNoPaths(false);
    setIsRequestLoading(true);

    requestedRef.current = true;

    attemptRequestPlan();
  };

  useEffect(() => {
    if (!verificationPathReady) return;
    if (requestedRef.current) return;

    runRequestPlan();
  }, [verificationPathReady]);

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  const selectedAttempt =
    attempts?.find((a) => a.attemptId === selectedAttemptId) ?? null;

  const firestoreErrorInfo = useMemo<VerificationErrorInfo | null>(() => {
    if (!firestoreError) return null;
    return buildVerificationErrorInfo({
      source: "firestore",
      cause: firestoreError,
    });
  }, [firestoreError]);

  const error =
    firestoreErrorInfo ??
    requestError ??
    (readinessError &&
      buildVerificationErrorInfo({
        source: "readiness",
        cause: readinessError,
      }));
  const isLoading = attemptsLoading || isRequestLoading || readinessLoading;

  return {
    selectedAttempt,
    isLoading,
    isRetrying,
    verificationPathReady,
    hasNoPaths,
    error,
    retryPlan: runRequestPlan,
  } as const;
};
